import { graphQlClient, graphQlUpload } from '@config/graphqlClient';
import SettingsGraphQL from '@graphql/settings.queries';
import UserGraphQL from '@graphql/user.queries';
import { User, UserLocale } from '@models/user.model';
import {
  Setting,
  SettingCreateInput,
  SettingUpdateInput,
} from '@models/setting.model';
import axios from 'axios';
import { getTokenLocalStorage, getTokenSessionStorage } from '@config/storage';

export const getAllSettings = async (): Promise<{ settings: Setting[] }> => {
  return await graphQlClient.request(SettingsGraphQL.queries.getAllSettings);
};

export const getSetting = async (
  settingId: string,
): Promise<{ setting: Setting }> => {
  if (!settingId) {
    throw new Error('Setting ID is required');
  }
  return await graphQlClient.request(SettingsGraphQL.queries.getSetting, {
    settingId,
  });
};

export const createSetting = async (
  input: SettingCreateInput,
): Promise<{ createSetting: Setting }> => {
  if (!input.name || !input.value) {
    throw new Error('Name and value are required');
  }
  return await graphQlClient.request(SettingsGraphQL.mutations.createSetting, {
    input,
  });
};

export const updateSetting = async (
  settingId: string,
  input: SettingUpdateInput,
): Promise<{ updateSetting: Setting }> => {
  if (!settingId) {
    throw new Error('Setting ID is required');
  }
  if (!input.name && !input.value && !input.description) {
    throw new Error('At least one field to update is required');
  }
  return await graphQlClient.request(SettingsGraphQL.mutations.updateSetting, {
    settingId,
    input,
  });
};

export const deleteSetting = async (
  settingId: string,
): Promise<{ deleteSetting: boolean }> => {
  if (!settingId) {
    throw new Error('Setting ID is required');
  }
  return await graphQlClient.request(SettingsGraphQL.mutations.deleteSetting, {
    settingId,
  });
};

/**
 * `UserUpdateInput.locale` is a GraphQL enum (`UserLocale`) since backend 854cbd3 (ISR-52) —
 * before that it was `String`, and the raw value went through. A GraphQL enum input is the
 * member NAME (`EN` / `ES`), not the value (`en` / `es`) that Redux stores and that the server
 * returns on `User.locale`. Sending the value is rejected at the API boundary with
 * `Value "es" does not exist in "UserLocale" enum`, which surfaced as the "Could not update
 * language" toast. The web `UserLocale` enum mirrors the backend's, so its keys are the wire names.
 */
export const toUserLocaleInput = (locale: UserLocale | string): string => {
  const name = (Object.keys(UserLocale) as (keyof typeof UserLocale)[]).find(
    (key) => UserLocale[key] === locale,
  );
  if (!name) {
    throw new Error(`Unsupported locale: ${locale}`);
  }
  return name;
};

export const updateCurrentUser = async (user: Partial<User>) => {
  const { firstName, lastName, userId, locale } = user;
  // Only send the fields that are being updated, not relationship fields
  const updatePayload: any = { userId };
  if (firstName !== undefined) updatePayload.firstName = firstName;
  if (lastName !== undefined) updatePayload.lastName = lastName;
  // Sprint 27, story-139. `locale` already exists on `UserUpdateInput` server-side
  // (user.update.input.ts:28) — the switcher is the first web caller to actually send it.
  if (locale !== undefined) updatePayload.locale = toUserLocaleInput(locale);
  // Sprint 33, story-169. Daily site report opt-out toggle.
  if (user.dailyReportEmailEnabled !== undefined) updatePayload.dailyReportEmailEnabled = user.dailyReportEmailEnabled;

  return await graphQlClient.request(UserGraphQL.mutations.updateOwnUser, {
    user: updatePayload,
  });
};

export const updatePassword = async (
  previousPassword: string,
  newPassword: string,
  rememberMe?: boolean,
) => {
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
  if (!token) {
    throw new Error('No auth token');
  }

  const url = `${process.env.REACT_APP_BACKEND_URL}auth/updatePassword`;
  const response = await axios.post(
    url,
    { previousPassword, newPassword },
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
};

export const updateAvatar = async (avatar: File) => {
  return await graphQlUpload(
    avatar,
    'user',
    UserGraphQL.mutations.updateOwnUser,
  );
};
